import { View } from 'foundations-components/components/View';
import { Carousel } from 'foundations-components/transitional/components/Carousel';
import styled from 'foundations-theming/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const animationDurationMs = 300;
const CarouselWrapper = styled.div.withConfig({
  displayName: "ClickableCarousel__CarouselWrapper"
})(({
  theme
}) => {
  const animationDuration = `${animationDurationMs}ms`;
  const animationTimingFunction = 'ease';
  const animationFillMode = 'forwards';
  return {
    '& [data-button-type="navigation"]': {
      border: 'none',
      background: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      blockSize: 'auto !important',
      inlineSize: 'auto !important',
      '&:hover': {
        background: 'transparent'
      },
      svg: {
        inlineSize: theme.space[600],
        blockSize: theme.space[600]
      },
      '*': {
        blockSize: 'auto !important'
      }
    },
    '& section': {
      padding: theme.space[0],
      overflowX: 'clip'
    },
    '@keyframes slideInFromRight': {
      from: {
        opacity: 0,
        transform: 'translateX(100%)'
      },
      to: {
        opacity: 1,
        transform: 'translateX(0)'
      }
    },
    '@keyframes slideInFromLeft': {
      from: {
        opacity: 0,
        transform: 'translateX(-100%)'
      },
      to: {
        opacity: 1,
        transform: 'translateX(0)'
      }
    },
    '& [data-transitioning="next"]': {
      animationName: 'slideInFromRight',
      animationDuration,
      animationTimingFunction,
      animationFillMode
    },
    '& [data-transitioning="prev"]': {
      animationName: 'slideInFromLeft',
      animationDuration,
      animationTimingFunction,
      animationFillMode
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& [data-transitioning]': {
        animationName: 'none !important'
      }
    }
  };
});
const ClickableCarousel = ({
  steps,
  stepIndex,
  onClick
}) => {
  const cardRefsMap = useRef(new Map());
  const transitionTimerRef = useRef(null);
  const directionRef = useRef('next');
  const isFirstRenderRef = useRef(true);

  // Clear transition timer on unmount to avoid calling removeAttribute on a detached node
  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) {
        clearTimeout(transitionTimerRef.current);
      }
    };
  }, []);
  const stepsWithRef = useMemo(() => steps.map(step => Object.assign({}, step, {
    content: /*#__PURE__*/_jsx("div", {
      ref: el => {
        if (el) {
          cardRefsMap.current.set(step.id, el);
        } else {
          cardRefsMap.current.delete(step.id);
        }
      },
      children: step.content
    })
  })), [steps]);

  // onMouseDown fires before the Carousel's internal click handler processes
  // onStepIndexChange, so directionRef is always up to date when it's read.
  const handleMouseDown = useCallback(evt => {
    var _icon$getAttribute;
    if (!(evt.target instanceof Element)) return;
    const navButton = evt.target.closest('[data-button-type="navigation"]');
    if (!navButton) return;
    const icon = navButton.querySelector('[data-icon-name]');
    const iconName = (_icon$getAttribute = icon === null || icon === void 0 ? void 0 : icon.getAttribute('data-icon-name')) !== null && _icon$getAttribute !== void 0 ? _icon$getAttribute : '';
    directionRef.current = iconName.toLowerCase().includes('left') ? 'prev' : 'next';
  }, []);
  const handleStepIndexChange = useCallback(evt => {
    onClick === null || onClick === void 0 || onClick(evt.target.value, directionRef.current);
  }, [onClick]);

  // Trigger slide-in animation after React re-renders the new card into the DOM.
  // The Carousel only mounts the active step's content, so we must wait until
  // stepIndex has updated and the new card's ref is attached before animating.
  useEffect(() => {
    var _cardRefsMap$current$;
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    const direction = directionRef.current;
    const activeStep = steps[stepIndex];
    if (!activeStep) return;
    const activeCard = (_cardRefsMap$current$ = cardRefsMap.current.get(activeStep.id)) !== null && _cardRefsMap$current$ !== void 0 ? _cardRefsMap$current$ : null;
    if (!activeCard) return;
    if (transitionTimerRef.current) {
      clearTimeout(transitionTimerRef.current);
    }
    activeCard.removeAttribute('data-transitioning');
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    void activeCard.offsetWidth; // force reflow so animation restarts on rapid clicks
    activeCard.setAttribute('data-transitioning', direction);
    transitionTimerRef.current = setTimeout(() => {
      activeCard.removeAttribute('data-transitioning');
    }, animationDurationMs);
  }, [stepIndex, steps]);
  return /*#__PURE__*/_jsx(View, {
    paddingInline: "400",
    paddingBlock: "1000",
    children: /*#__PURE__*/_jsx(CarouselWrapper, {
      onMouseDown: handleMouseDown,
      children: /*#__PURE__*/_jsx(Carousel, {
        allowStepLoop: true,
        stepIndex: stepIndex,
        onStepIndexChange: handleStepIndexChange,
        steps: stepsWithRef
      })
    })
  });
};
export default ClickableCarousel;